import { Injectable } from '@angular/core';
import { LayersBtn, OverlayLayer } from '@dashboard/dashboard-store';
import { CRS, Map, TileLayer, WMSOptions, tileLayer } from 'leaflet';
import lodash from 'lodash';
import { EnvVarsService } from 'src/app/env-vars.service';
import { ThemeService } from 'src/app/services/theme.service';

type layers = { baseLayers: LayersBtn; overlay: LayersBtn };
type ActiveLayerConfig = OverlayLayer['layerGroup'][number];
type ThemedWmsParams = {
  layers: string;
  styles?: string;
  transparent?: boolean;
  bgcolor?: string;
};
type GeoServerWmsOptions = WMSOptions & {
  tiled?: boolean;
};

@Injectable({
  providedIn: 'root',
})
export class MapLayersService {
  private MAP_URL = this.environment.getEnvKey('MAP_URL');
  private CARTO_API_KEY = this.environment.getEnvKey('CARTO_API_KEY');
  private basemaps: Record<string, TileLayer> = {};
  private _map: Map;
  private _activeBaseLayer = '';
  private activeLayerOrder: string[] = [];
  private activeLayerConfigs: Record<string, ActiveLayerConfig> = {};

  private previewsLayers: string[] = [];

  constructor(private environment: EnvVarsService, private theme: ThemeService) {
    this.theme.isDark$.subscribe(() => this._applyTileTheme());
  }

  private WMS_PROPS: GeoServerWmsOptions = {
    format: 'image/png', // Specify the image format
    version: '1.3.0', // Specify the WMTS version
    tiled: true, // GeoServer vendor param for tiled WMS requests
    // transparent: false, // Set transparency if your layer has transparency - now handled dynamically
    // tileSize: 256, // Tile size
    // crs: CRS.EPSG4326, // Use the appropriate CRS
    crs: CRS.EPSG3857, // Use the appropriate CRS
    attribution: 'Konnecta',
  };

  private _MapLayers!: layers;

  public set setMapLayers(layers: layers) {
    console.log(layers);

    this._MapLayers = layers;
  }

  public set Map(map: Map) {
    this._map = map;
  }

  private RemoveLayers(keys: string[]): void {
    console.log('RemoveLayers', keys);

    if (
      keys.length === 0 ||
      !this.basemaps ||
      Object.keys(this.basemaps).length === 0
    )
      return;
    keys.map((key) => {
      console.log('Removing layer', key);
      this._map.removeLayer(this.basemaps[key]);
      delete this.basemaps[key];
    });
  }

  public selectLayer(id: string): void {
    this._map.addLayer(this.basemaps[id]);
  }
  public ReInstantiateNaviqoreLayers(layers: {
    baseLayer: string;
    overlayLayers: string[];
  }) {
    const baseMapLayer: OverlayLayer =
      this._MapLayers.baseLayers.layers[layers.baseLayer];

    const overlayMapLayers: OverlayLayer[] =
      this._MapLayers.overlay.layers.filter((_, index) =>
        layers.overlayLayers.map((d) => Number(d)).includes(index)
      );
    const zoomLvl = Math.round(this._map.getZoom() * 10) / 10;

    const baseLayerConfigsPerZoom = this.getLayerConfigsForZoom(
      [baseMapLayer],
      zoomLvl
    );
    const overlayLayerConfigsPerZoom = this.getLayerConfigsForZoom(
      overlayMapLayers,
      zoomLvl
    );
    const belowBaseLayerConfigs = overlayLayerConfigsPerZoom.filter(
      (layer) => layer.renderBelowBase
    );
    const aboveBaseLayerConfigs = overlayLayerConfigsPerZoom.filter(
      (layer) => !layer.renderBelowBase
    );
    const layerConfigsPerZoom = [
      ...belowBaseLayerConfigs,
      ...baseLayerConfigsPerZoom,
      ...aboveBaseLayerConfigs,
    ];

    const layersPerZoom = layerConfigsPerZoom.map((d) => d.layer);
    this.activeLayerOrder = layersPerZoom;
    this.activeLayerConfigs = layerConfigsPerZoom.reduce((acc, layer) => {
      acc[layer.layer] = layer;
      return acc;
    }, {} as Record<string, ActiveLayerConfig>);

    // console.log('Layers to be active', zoomLvl, layersPerZoom);
    // find what layers to remove and what layers to add

    const missingLayers = this.FindMissingValues(
      layersPerZoom,
      this.previewsLayers
    );

    this.RemoveLayers(missingLayers);

    this.previewsLayers = layersPerZoom;

    this.basemaps = {
      ...this.SetActiveLayers(layerConfigsPerZoom),
      ...this.basemaps,
    };

    Object.keys(this.basemaps).map((layer) => {
      //if already enabled stop
      if (this._map.hasLayer(this.basemaps[layer])) {
        console.log(`Layer - ${layer} is laready active`);
        return;
      }
      // console.log(`Enabling - ${layer}`);
      this.selectLayer(layer);
    });

    this.activeLayerOrder.forEach((layer, index) => {
      const mapLayer = this.basemaps[layer] as any;
      if (!mapLayer) return;

      mapLayer.setZIndex?.(index);
    });

    this._activeBaseLayer = baseLayerConfigsPerZoom[0]?.layer ?? '';
    this._applyTileTheme();
  }

  private getLayerConfigsForZoom(
    layers: OverlayLayer[],
    zoomLvl: number
  ): ActiveLayerConfig[] {
    return layers.reduce((acc, layer) => {
      layer.layerGroup.forEach((d) => {
        if (d.startZoom <= zoomLvl && d.endZoom > zoomLvl) {
          acc.push(d);
        }
      });
      return acc;
    }, [] as ActiveLayerConfig[]);
  }

  private _applyTileTheme(): void {
    if (!this._map) return;

    const tilePane = this._map.getPane('tilePane');
    const activeBaseConfig = this.activeLayerConfigs[this._activeBaseLayer];
    const activeBaseLayer = activeBaseConfig
      ? this.getThemedLayer(activeBaseConfig)
      : this._activeBaseLayer;
    const isLightCartoUrl =
      !this.theme.isDark && activeBaseLayer.includes('cartocdn.com');

    if (tilePane) {
      // Subtle desaturation for light-mode Carto — never invert()
      tilePane.style.filter = isLightCartoUrl
        ? 'saturate(0.75) brightness(1.02)'
        : '';
    }

    Object.entries(this.activeLayerConfigs).forEach(([layerKey, config]) => {
      const mapLayer = this.basemaps[layerKey] as any;
      if (!mapLayer) return;

      if (this.isExternalTileLayer(layerKey)) {
        mapLayer.setUrl(this.withCartoApiKey(this.getThemedLayer(config)));
      } else if (mapLayer.setParams) {
        mapLayer.setParams(
          this.getThemedWmsParams(
            config,
            layerKey === this.activeLayerOrder[0],
            layerKey !== this.activeLayerOrder[0]
          )
        );
      }
    });
  }

  public ReInstantiateLayers(layers: {
    baseLayer: string;
    overlayLayers: string[];
  }) {
    const isCustomMap =
      this._MapLayers.baseLayers.layers[
        Number(layers.baseLayer)
      ].layerGroup[0].layer.includes('http');

    layers.overlayLayers = isCustomMap ? [] : layers.overlayLayers;

    this.ReInstantiateNaviqoreLayers(layers);
  }

  private FindMissingValues(curr: string[], prev: string[]) {
    return lodash.difference(prev, curr);
  }

  private SetActiveLayers(layers: ActiveLayerConfig[]) {
    return layers.reduce((acc, layerConfig, index) => {
      const layer = layerConfig.layer;
      const themedLayer = this.getThemedLayer(layerConfig);
      const isCustomMap = this.isExternalTileLayer(layer);
      const isBaseLayer = index === 0; // First layer is always the base layer

      acc[layer] = isCustomMap
        ? this.GenerateLeafletTileLayer(themedLayer)
        : this.GenerateWMSTileLayer(layerConfig, isBaseLayer);
      return acc;
    }, {} as Record<string, TileLayer>);
  }

  private getThemedLayer(layerConfig: ActiveLayerConfig): string {
    return this.theme.isDark && layerConfig.layerDark
      ? layerConfig.layerDark
      : layerConfig.layer;
  }

  private getThemedBgColor(layerConfig: ActiveLayerConfig): string | undefined {
    const bgColor = this.theme.isDark && layerConfig.bgColorDark
      ? layerConfig.bgColorDark
      : layerConfig.bgColor;

    return this.normalizeWmsColor(bgColor);
  }

  private getThemedStyle(layerConfig: ActiveLayerConfig): string | undefined {
    return this.theme.isDark && layerConfig.styleDark
      ? layerConfig.styleDark
      : layerConfig.style;
  }

  private getThemedWmsParams(
    layerConfig: ActiveLayerConfig,
    includeBgColor: boolean = false,
    transparent?: boolean
  ): ThemedWmsParams {
    const bgcolor = includeBgColor
      ? this.getThemedBgColor(layerConfig)
      : undefined;
    const styles = this.getThemedStyle(layerConfig);

    return {
      layers: layerConfig.layer,
      ...(styles ? { styles } : {}),
      ...(transparent !== undefined ? { transparent } : {}),
      ...(bgcolor ? { bgcolor } : {}),
    };
  }

  private isExternalTileLayer(layer: string): boolean {
    return layer.includes('http');
  }

  private normalizeWmsColor(color?: string): string | undefined {
    if (!color) return undefined;

    return color.startsWith('#') ? `0x${color.slice(1)}` : color;
  }

  private GenerateLeafletTileLayer(layer: string) {
    const isCarto = layer.includes('cartocdn.com');
    return tileLayer(isCarto ? this.withCartoApiKey(layer) : layer, {
      subdomains: isCarto ? 'abcd' : 'abc',
      maxZoom: 19,
      attribution: isCarto
        ? '© <a href="https://www.openstreetmap.org/copyright">OSM</a> · © <a href="https://carto.com/attributions">CARTO</a>'
        : '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    });
  }

  private withCartoApiKey(tileUrl: string): string {
    if (
      !this.CARTO_API_KEY ||
      tileUrl.includes('?key=') ||
      tileUrl.includes('&key=')
    ) {
      return tileUrl;
    }

    const separator = tileUrl.includes('?') ? '&' : '?';
    return `${tileUrl}${separator}key=${encodeURIComponent(this.CARTO_API_KEY)}`;
  }

  private GenerateWMSTileLayer(
    layerConfig: ActiveLayerConfig,
    isBaseLayer: boolean = false
  ) {
    return tileLayer.wms(this.getWmsUrl(), {
      ...this.WMS_PROPS,
      transparent: !isBaseLayer, // Base layer: false, overlays: true
      ...this.getThemedWmsParams(layerConfig, isBaseLayer),
    });
  }

  private getWmsUrl(): string {
    return this.withoutQueryParam(this.MAP_URL, 'bgcolor');
  }

  private withoutQueryParam(url: string, paramName: string): string {
    const [baseUrl, queryString] = url.split('?');
    if (queryString === undefined) return url;

    const params = queryString
      .split('&')
      .filter(Boolean)
      .filter((param) => param.split('=')[0].toLowerCase() !== paramName);

    return params.length > 0 ? `${baseUrl}?${params.join('&')}` : `${baseUrl}?`;
  }
  public Initialize() {
    return;
  }
}
